import React, { useEffect } from 'react';

import { Button } from '../Button/Button';
import { Icon } from '../Icon/Icon';
import { Text } from '../Text/Text';
import { IconName } from '../Icon/Icon.type';
import { TButtonSize } from '../Button/Button.type';

import type { IModalProps, TModalSize } from './Modal.types';

export const Modal: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  onSubmit,
  onCancel,
  submitLabel = 'Submit',
  cancelLabel = 'Cancel',
  submitDisabled = false,
  submitLoading = false,
  submitVariant = 'primary',
  submitClassName,
  buttonSize,
  hideFooter = false,
  className,
}: IModalProps) => {
  
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeMapper = {
    'sm': 'max-w-md',
    'md': 'max-w-lg',
    'lg': 'max-w-2xl',
    'xl': 'max-w-4xl',
  };

  const buttonSizeMapper: Record<TModalSize, TButtonSize> = {
    'sm': 'small',
    'md': 'medium',
    'lg': 'large',
    'xl': 'large',
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose?.();
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onClose?.();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-bg-v2-lg bg-alpha-black-70"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div
        className={`bg-bg-primary rounded-bg-v2-xl shadow-bg-v2-2xl w-full ${sizeMapper[size]} flex flex-col max-h-[90vh] relative ${className ?? ''}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-bg-v2-2xl border-b border-border-secondary">
          {title && (
            <Text
              variant="text-lg"
              weight="semibold"
              color="primary"
              as="h2"
            >
              {title}
            </Text>
          )}
          {onClose && <button
            onClick={onClose}
            className="ml-auto text-fg-secondary hover:bg-bg-primary-hover transition-colors p-bg-v2-xs rounded-full"
            aria-label="Close modal"
          >
            <Icon name={IconName.Cross} size={size} inheritColor />
          </button>}
        </div>

        {/* Body */}
        <div className="p-bg-v2-2xl overflow-y-auto flex-1 scrollbar-spavced ">
          {children}
        </div>

        {/* Footer */}
        {!hideFooter && (
          <div className="flex items-center justify-end gap-bg-v2-md p-bg-v2-2xl border-t border-border-secondary">
            {onCancel && (
              <Button
                label={cancelLabel}
                variant="outline"
                onClick={handleCancel}
                disabled={submitLoading}
                size={buttonSize ?? buttonSizeMapper[size]}
              />
            )}
            {onSubmit && (
              <Button
                label={submitLabel}
                variant={submitVariant}
                onClick={onSubmit}
                disabled={submitDisabled}
                loading={submitLoading}
                size={buttonSize ?? buttonSizeMapper[size]}
                className={submitClassName}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};